import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { X, Plus, Loader2, Waypoints } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { LOOP_TYPES, LOOP_LEVELS, LOOP_SCRAPERS } from "@/lib/loopConstants";

const EMPTY = { title: "", location: "", type: "Full-time", level: "Senior", matchlevel: 50, keywords: [], scrapers: ["LinkedIn"] };

export default function LoopBuilder({ open, onClose, onSaved, mode = "create", initial = null, loopRef = null, options }) {
  const [form, setForm] = useState(EMPTY);
  const [kw, setKw] = useState("");
  const [saving, setSaving] = useState(false);

  const types = options?.types || LOOP_TYPES;
  const levels = options?.levels || LOOP_LEVELS;
  const scrapers = options?.scrapers || LOOP_SCRAPERS;

  useEffect(() => {
    if (open) {
      setForm(initial ? {
        title: initial.title || "",
        location: initial.location || "",
        type: initial.type || "Full-time",
        level: initial.level || "Senior",
        matchlevel: initial.matchlevel ?? 50,
        keywords: initial.keywords || [],
        scrapers: initial.scrapers || [],
      } : EMPTY);
      setKw("");
    }
  }, [open, initial]);

  const addKw = () => {
    const v = kw.trim();
    if (v && !form.keywords.includes(v)) setForm({ ...form, keywords: [...form.keywords, v] });
    setKw("");
  };

  const toggleScraper = (s) => {
    setForm((f) => ({
      ...f,
      scrapers: f.scrapers.includes(s) ? f.scrapers.filter((x) => x !== s) : [...f.scrapers, s],
    }));
  };

  const submit = async () => {
    if (!form.title.trim() || !form.location.trim()) {
      toast.error("Title and location are required.");
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      location: form.location.trim(),
      type: form.type,
      level: form.level,
      matchlevel: Number(form.matchlevel),
      keywords: form.keywords,
      scrapers: form.scrapers,
    };
    try {
      let res;
      if (mode === "edit" && loopRef) {
        res = await api.put(`/loops/${loopRef}`, payload);
        toast.success("Search updated.");
      } else {
        res = await api.post("/loops", payload);
        toast.success("Apply Series search created.");
      }
      onSaved?.(res.data.loop);
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-white border border-slate-200 text-slate-900 sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="loop-builder-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Waypoints className="w-5 h-5 text-amber-500" />
            {mode === "edit" ? "Edit Loop" : "Build a Loop"}
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            {mode === "edit"
              ? "Modify the search configuration. Changes take effect on the next collection run."
              : "Create an Apply Series search. The engine collects matching jobs and filters them by your criteria."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-slate-700 text-sm">Job title <span className="text-rose-400">*</span></Label>
            <Input data-testid="loop-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Software Engineer" className="mt-1.5 bg-slate-50 border-slate-300 h-11 focus-visible:ring-amber-500/40" />
          </div>
          <div>
            <Label className="text-slate-700 text-sm">Location <span className="text-rose-400">*</span></Label>
            <Input data-testid="loop-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="London, United Kingdom" className="mt-1.5 bg-slate-50 border-slate-300 h-11 focus-visible:ring-amber-500/40" />
            <p className="text-xs text-slate-500 mt-1">Format: city, country</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-700 text-sm">Employment type <span className="text-rose-400">*</span></Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="loop-type-select" className="mt-1.5 bg-slate-50 border-slate-300 h-11"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-800">
                  {types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-700 text-sm">Experience level <span className="text-rose-400">*</span></Label>
              <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                <SelectTrigger data-testid="loop-level-select" className="mt-1.5 bg-slate-50 border-slate-300 h-11"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 text-slate-800">
                  {levels.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-slate-700 text-sm">Match level</Label>
              <span className="text-sm font-mono text-amber-400">{form.matchlevel}</span>
            </div>
            <Slider data-testid="loop-matchlevel-slider" value={[form.matchlevel]} min={0} max={100} step={5}
              onValueChange={(v) => setForm({ ...form, matchlevel: v[0] })} className="mt-3" />
            <p className="text-xs text-slate-500 mt-1">Higher = stricter matching against your criteria.</p>
          </div>
          <div>
            <Label className="text-slate-700 text-sm">Keywords</Label>
            <div className="flex gap-2 mt-1.5">
              <Input data-testid="loop-keyword-input" value={kw} onChange={(e) => setKw(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addKw(); } }}
                placeholder="python, react…" className="bg-slate-50 border-slate-300 h-11 focus-visible:ring-amber-500/40" />
              <Button type="button" variant="outline" onClick={addKw} className="border-slate-300 h-11" data-testid="loop-add-keyword"><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {form.keywords.map((k) => (
                <Badge key={k} className="bg-orange-500/10 text-amber-400 border border-orange-500/30 gap-1 py-1">
                  {k}<button onClick={() => setForm({ ...form, keywords: form.keywords.filter((x) => x !== k) })}><X className="w-3 h-3" /></button>
                </Badge>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-slate-700 text-sm">Job sources (scrapers)</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {scrapers.map((s) => (
                <button key={s} type="button" data-testid={`loop-scraper-${s}`} onClick={() => toggleScraper(s)}
                  className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                    form.scrapers.includes(s)
                      ? "border-orange-500/60 bg-orange-500/15 text-orange-200"
                      : "border-slate-300 text-slate-500 hover:text-slate-800"
                  }`}>{s}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button variant="ghost" onClick={onClose} className="text-slate-500">Cancel</Button>
          <Button data-testid="loop-submit-button" onClick={submit} disabled={saving}
            className="bg-amber-500 text-black hover:bg-amber-400 font-semibold transition-colors">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Waypoints className="w-4 h-4 mr-2" />}
            {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Create Loop"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
